<template>

  <div class="text-content">
    <h2 class="text-2xl mb-6">Sorenson Forum - Terms of Service</h2>
    <p>
      <strong>
        IMPORTANT: THESE TERMS OF SERVICE CONTAIN A BINDING ARBITRATION
        AGREEMENT AND CLASS ACTION WAIVER IN THE SECTION TITLED DISPUTE
        RESOLUTION, WHICH AFFECTS YOUR RIGHTS WITH RESPECT TO ANY DISPUTES
        BETWEEN US AND YOU, AND REQUIRE YOU TO RESOLVE DISPUTES IN BINDING,
        INDIVIDUAL ARBITRATION AND NOT IN COURT.  BY AGREEING TO THESE TOS, YOU
        ARE WAIVING YOUR RIGHT TO SEEK RELIEF IN A COURT OF LAW, WAIVING YOUR
        RIGHT TO HAVE A JURY TRIAL ON YOUR CLAIMS AND EXPRESSLY ACKNOWLEDGING
        THAT YOU HAVE READ AND UNDERSTOOD THESE TERMS OF SERVICE AND HAVE TAKEN
        TIME TO CONSIDER THE CONSEQUENCES OF THIS IMPORTANT DECISION.
      </strong>
    </p>
    <p>
      Welcome, and thank you for your interest in our transcription and
      translation application for meetings, conferences, events, and experiences
      (the “<strong>Services</strong>”) available through our website
      <a :href="websiteUrl">
        {{ websiteUrl }}
      </a>
      , its subdomains (collectively, the “<strong>Website</strong>”), and related
      mobile applications (each, an “<strong>App</strong>”). These Terms of
      Services (the “<strong>TOS</strong>” or “<strong>Agreement</strong>”)
      together with the Terms of Use governing the use of our Website located at
      <a :href="termsOfUseUrl">
        {{ termsOfUseUrl }}
      </a>
      (which are incorporated by reference herein) (the “<strong>TOU</strong>”)
      form a legally binding agreement between Sorenson Communications, LLC
      (together without its affiliates, “<strong>Sorenson</strong>”, "<strong>us</strong>",
      "<strong>our</strong>" or "<strong>we</strong>") and you, your heirs,
      assigns, and successors ("<strong>you</strong>" or "<strong>your</strong>")
      as the user of our Services via the App and/or our Website.  In the event of
      a conflict between the TOU and these TOS, the terms of these TOS will
      control.
    </p>
    <p>
      BY CLICKING ON I AGREE, SIGNING UP FOR AN ACCOUNT, DOWNLOADING THE APP, OR
      ACCESSING OR USING THE SERVICES, YOU ARE ENTERING INTO THESE TOS AND AGREE
      TO BE BOUND BY THESE TOS, AND OUR PRIVACY POLICY.  PLEASE READ THESE TOS
      CAREFULLY.  DO NOT SIGN UP FOR AN ACCOUNT OR USE THE SERVICES AND UNINSTALL
      THE APP IF YOU ARE UNWILLING OR UNABLE TO BE BOUND BY THESE TOS.
    </p>
    <p>
      If you are using our Services on behalf of a company or other organization,
      you represent and warrant that you have authority to act on behalf of that
      organization and to bind that organization to these TOS, that the
      organization is duly organized and legally in good standing, and that you
      understand that “you” and “your” as used herein shall refer to that
      organization, its directors, officers, employees, agents and all associated
      users.  If your organization has entered into a separate written agreement
      with Sorenson covering your organization’s use of the services, then that
      separate written agreement will apply instead of these TOS.
    </p>
    <p>
      We may change this Agreement at any time by posting the updated Agreement on
      the Website, or in the App, with or without notice to you. If we materially
      modify any portion of these TOS, we may notify you at the email address
      provided in your Account profile or by posting a notice on the Website or in
      the App).  Your continued use of the Services after the TOS has been updated
      or changed constitute your acceptance of the revised TOS.  If you do not
      agree to any such modifications, you must cease using the Services. By your
      ongoing use of the Services, you are constructively deemed to have accepted
      the modifications.
    </p>
    <p>
      <strong>1. Eligibility for Our Service.</strong>
    </p>
    <p>
      Our Services are intended for users 18 years of age or older. By using our
      Services, you represent and warrant that you have attained the age of
      majority where you reside (18 years of age in most jurisdictions) and are
      otherwise capable of entering into binding contracts including this
      Agreement.
    </p>
    <p>
      <strong>2. Privacy Policy.</strong>
    </p>
    <p>
      In order to provide or operate the Services, we will collect certain
      information about you. Your use of the Services is subject to our
      <a :href="privacyPolicyUrl">
        Privacy Policy
      </a>
      . The
      <a :href="privacyPolicyUrl">
        Privacy Policy
      </a>
      describes how we collect, use, share and disclose information about you when
      you use our Services.  By using the Services, you are consenting to the
      terms of the
      <a :href="privacyPolicyUrl">
        Privacy Policy
      </a>
      and acknowledge that you have reviewed our
      <a :href="privacyPolicyUrl">
        Privacy Policy
      </a>
      .
    </p>
    <p>
      <strong>3. Account and Registration.</strong>
    </p>
    <p>
      <strong>a.  Service.</strong>The Services are transcription and translation
      services for use on a mobile device or computer for meetings, conferences,
      events, individual conversations and experiences to translate, in real time,
      any supported language made available to any user with an Account (the
      “<strong>Registered Users</strong>”) as defined below) or any participant
      with access to the applicable session code (a “<strong>Participant</strong>”,
      together with the Registered Users, the “<strong>Users</strong>”).
    </p>
    <p>
      <strong>b.  Accounts.</strong> To access certain features of the Services,
      such as the  premium features (e.g. hosting a session or adding to a custom
      glossary), you are required to register for an account (an “<strong>Account</strong>”),
      which includes the creation of a username and password (the “<strong>Credentials</strong>”).
      There are no Fees required to create an Account, but Fees will apply if you
      sign-up for a paid Plan under your Account.  When you register for an
      Account, you will be required to provide us with some information about
      yourself (such as your name, date of birth, e-mail address, phone number or
      other personal information). Some of this information may include personally
      identifying information (all "<strong>PII</strong>" or "<strong>Account
      Information</strong>"). We may terminate or suspend your Account if you fail
      to provide us with accurate and complete registration and account
      information. You agree to keep your Account and Account Information up to
      date, including updating your contact information and providing and
      maintaining accurate and lawful billing information on your Account. You
      agree to: (a) not share your Account or transfer any part of it to anyone
      else; (b) provide accurate, current and complete information during the
      registration process when providing us with PII for any reason; and (c) keep
      your Credentials secure and confidential.  You are solely responsible for
      any transactions or uses undertaken by means of your Credentials and under
      your Account. You will notify us immediately of any unauthorized use of or
      access to your Account or Credentials. In such a case, you understand and
      agree that we may terminate or suspend your Account and/or transactions
      undertaken through your Account.  We are not liable for any losses by any
      party caused by any unauthorized use of your Account or Credentials.  We
      reserve the right to refuse to issue an Account to anyone or permit access
      to the Services to anyone for any reason at any time.
    </p>
    <p>
      <strong>c. Contacting You.</strong> You consent to us sending you
      Service-related notices to the email address you provide in your Account,
      including any notices required by law, in lieu of communication by other
      means such as postal mail. We may also use your email address to send you
      other messages, such as changes to Service features, special offers, or
      Service-related newsletters. If you do not want to receive marketing emails,
      you may opt out or change your preferences by following the instructions on
      the applicable email message. Opting out may prevent you from receiving
      email messages regarding updates, improvements or offers. Even if you opt
      out of marketing emails, we may still send you administrative emails
      regarding the Services, including, for example, notices of updates to these
      TOS or our Privacy Policy, and information related to your Account.
    </p>
    <p>
      <strong>4. Rights To Use, Restrictions and Acceptable Use.</strong>
    </p>
    <p>
      <strong>a. Rights to Use.</strong> Subject to your full and continued
      compliance with this TOS, during the Plan Period, we grant you, a personal,
      non-exclusive, revocable, non-sublicensable, non-transferable limited right
      to (i) access and use the Services subject to the usage restrictions in the
      plan that you have sign up for as reflected in your Account (the “Plan”),
      and (ii) install, run and use the App on a device that you own and control,
      in executable, machine-readable, object code form only, and (iii) access all
      documentation related to the Service that we make available to you from time
      to time (the “<strong>Documentation</strong>”), solely in connection with
      your authorized use of the Services. Any other use is expressly prohibited.
      This grant is revocable at any time without notice and with or without
      cause.
    </p>
    <p>
      <strong>b.  Usage Limitations.</strong> The Service may be subject to
      certain additional limitations, such as (a) limits on the number of
      Participants per session; (b) limits to the amount of interpretation time,
      or (c) limits to the number of sessions.  Access to our Services may not be
      available in all locations. You are only entitled to access and use our
      Services for lawful purposes and pursuant to this Agreement and our
      <a :href="privacyPolicyUrl">
        Privacy Policy
      </a>
      . Any action by you that, in our sole discretion: (i) violates the terms and
      conditions of this Agreement, the TOU and/or the
      <a :href="privacyPolicyUrl">
        Privacy Policy
      </a>
      ; (ii) restricts, inhibits or prevents any access, use or enjoyment of our
      Services; or (iii) through the use of our Services, defames, abuses,
      harasses, offends or threatens others, shall not be permitted, and may
      result in your loss of the right to access and use our Services.
    </p>
    <p>
      <strong>c. Usage Restrictions.</strong> The rights granted to you in this
      Agreement are subject to the following restrictions, and you agree not to
      (nor to permit any other party to): (i) sublicense, sell, rent, lease,
      transfer, assign, distribute, host, provide access to the Services as a
      service bureau or similar fee-for-service purpose, or otherwise commercially
      exploit the Services; (ii) modify, copy, make derivative works of,
      disassemble, decompile, attempt to derive the source code of or gain
      unauthorized access to, or reverse engineer any part of the App or Services;
      (iii) access the Services in order to build a similar or competitive
      service; (iv) bypass any robot exclusion headers or other measures we may
      use to prevent or restrict access to our Services; (v) interfere with or
      disrupt the Services or components thereof; (vi) take any action that
      negatively affects the ability of others to access or use the Services;
      (vii) use the Services for or in any fraudulent, illegal, inappropriate, or
      unauthorized purpose or manner, including without limitation, violation of
      any person’s privacy rights or infringes the right to any third party;
      (viii) introduce any viruses, malware or harmful code to the Services; (ix)
      take any action that imposes, or may impose (in our sole discretion) an
      unreasonable or disproportionately large load on our infrastructure; or (x)
      collect, harvest, copy, reproduce, modify, create derivative works from,
      distribute or publicly display any content or personally identifiable
      information (except for your own personal information) from our Services
      without our prior written permission and the appropriate third party, as
      applicable.  You agree not to engage or participate in communications or
      conduct of an abusive, pornographic, lewd, obscene, harassing, fraudulent,
      or unlawful nature while using the Services.  In addition, you agree not to
      solicit for commercial purposes any Users of our Services without our prior
      written permission. We may, at our option, review your Account and take any
      action we deem appropriate, including suspending or terminating your access
      to the Service or your Account if we determine you are using the Service
      contrary to this Usage Restrictions provision or these TOS generally.
    </p>
    <p>
      <strong>d. Modifications.</strong> Your use of the Services includes the
      right to access all functionality available in the Plan to which you are
      subscribed to.  We reserve the right to modify or discontinue the Services
      or any feature or functionality thereof at any time without notice to you.
      We reserve the right to change the fees applicable to the Services at any
      time without notice. Our pricing will be reflected in the App and Website,
      and your continued use of the Services after the price change becomes
      effective will constitute your agreement to pay the changed amount;
      provided, however, that if pricing modifications would negatively impact
      your then-current Plan Period, these modifications will not apply to you
      until the start of the next Plan Period (if applicable). To the extent we
      make available any new or different features, functionality or enhancements
      to the Services, we may market these separately and may require the payment
      of additional fees. You agree that we will not be liable to you or any third
      party for any modification of the Services.
    </p>
    <p>
      <strong>e. Beta Versions.</strong> Certain features of the Service may be
      offered as a beta version (“<strong>Beta Version</strong>”). The fact and
      existence of any Beta Version will be deemed to be our Confidential
      Information under this Agreement. You are not required to use any Beta
      Version and do so at your sole risk.  We might not charge for the Beta
      Version but reserve the right to charge for subsequent versions, including
      any potential commercial releases. You acknowledge and agree that the Beta
      Version may contain, in our sole discretion, more or fewer features or
      different licensing terms than a subsequent commercial release version of
      the Beta Version. We reserve the right not to release commercial release
      versions of the Beta Version. Without limiting any disclaimer of warranty or
      other limitation stated in this Agreement (or any separate terms and
      conditions that would otherwise be applicable to such Beta Versions), you
      agree that Beta Versions are not considered by us to be suitable for
      commercial use, and that they may contain errors affecting their proper
      operation. We are not obligated to provide any maintenance, technical, or
      other support for the Beta Versions.  YOU HEREBY ACKNOWLEDGE AND AGREE THAT
      ANY BETA VERSIONS ARE PROVIDED ON AN “AS IS” AND AS AVAILABLE BASIS, AND
      YOUR ACCESS TO AND/OR USE OF THE BETA VERSIONS IS AT YOUR SOLE RISK.  YOU
      ACKNOWLEDGE AND AGREE THAT USE OF ANY BETA VERSION MAY EXHIBIT SPORADIC
      DISRUPTIONS THAT HAVE THE POTENTIAL TO DISRUPT YOUR USE OF ANY SERVICES.
      USE OF THE BETA VERSION IS AT YOUR OWN RISK.
    </p>
    <p>
      <strong>f. Trial Plans.</strong> Trial Plans are available for a one-time
      limited trial Plan Period and are subject to limitations, as specified on
      our Website. You must sign-up for an Account to enroll in a trial Plan. The
      duration of your trial Plan Period is set forth on our Website when you
      sign-up for the trial Plan.  At the end of the trial Plan Period, unless you
      have upgraded to a paid Plan, the Services will automatically terminate.
      Trial Plan Periods may be extended at our sole discretion.  Notwithstanding
      anything to the contrary herein or otherwise, UNDER A TRIAL PLAN, (I) THE
      SERVICESARE PROVIDED “AS-IS”, WITHOUT ANY WARRANTIES, INDEMNIFICATION, OR
      LIABILITY OF ANY KIND; (II) WE DISCLAIM ALL WARRANTIES, WHETHER EXPRESS,
      IMPLIED, STATUTORY, OR OTHERWISE, WITH RESPECT TO ANY TRIAL, AND (III) WE
      WILL NOT BE LIABLE FOR ANY DAMAGES, WHETHER DIRECT, INDIRECT, INCIDENTAL,
      CONSEQUENTIAL, OR OTHERWISE, ARISING OUT OF OR IN CONNECTION WITH YOUR USE
      OF THE SERVICES. We reserve the right to refuse or terminate your trial in
      our sole discretion, and in such event, you hereby agree to forfeit any and
      all rights to any User Content created during such trial Plan Period.
    </p>
    <p>
      <strong></strong>
    </p>
    <p>
      <strong>5. Additional App Terms</strong>
    </p>
    <p>
      <strong>a. App Usage Generally.</strong> To use the App to access the
      Services, you must have a mobile device that is compatible with the
      applicable App. We do not warrant that an App will be compatible with your
      mobile device. You may use mobile data in connection with the Apps and may
      incur additional charges from your wireless provider for the use of the
      Services. You agree that you are solely responsible for any such charges.
      You acknowledge that we may from time-to-time issue upgraded versions of the
      App and may automatically electronically upgrade the version of the App that
      you are using on your mobile device, and you consent to receiving automatic
      upgrades on your mobile device.  You also acknowledge and agree that
      standard carrier data charges may apply to your use of the Services.
    </p>
    <p>
      <strong>b.  Apple App Store Terms.</strong> The following applies if you
      download the App from or access the App through the Apple App Store (the
      “<strong>Third Party App Store</strong>”):
    </p>
    <p>
      i.  You acknowledge and agree that (i) this Agreement is concluded between
      you and Sorenson only, and not the Third Party App Store, and (ii) Sorenson,
      not the Third Party App Store, is solely responsible for the Application and
      content thereof. Your use of the Services must comply with the
      <a href="https://www.apple.com/legal/internet-services/itunes/">
        Third Party App Store Terms of Service
      </a>
      .  Any questions, complaints or claims with respect to the App should be
      directed to Sorenson’s customer support team at
      <a :href="`mailto:${supportEmail}`">
        {{ supportEmail }}
      </a>
      .
    </p>
    <p>
      ii.  You acknowledge that the Third Party App Store has no obligation
      whatsoever to furnish any maintenance and support services with respect to
      the App.
    </p>
    <p>
      iii.  In the event of any failure of the App to conform to any applicable
      warranties set forth herein, you may notify the Third Party App Store, and
      the Third Party App Store will refund the purchase price paid to the Third
      Party App Store by you for the App (if any), and to the maximum extent
      permitted by applicable law, the Third Party App Store will have no other
      warranty obligation whatsoever with respect to the App. As between Sorenson
      and the Third Party App Store, any other claims, losses, liabilities,
      damages, costs or expenses attributable to any failure to conform to any
      warranty in this Agreement will be the sole responsibility of Sorenson.
    </p>
    <p>
      iv.  We and you both acknowledge that, as between Sorenson and the Third
      Party App Store, the Third Party App Store is not responsible for addressing
      your claims or any claims of any third party relating to the App or your
      possession and/or use of the App, including, but not limited to: (i) product
      liability claims; (ii) any claim that the App fails to conform to any
      applicable legal or regulatory requirement; and (iii) claims arising under
      consumer protection laws or similar legislation.
    </p>
    <p>
      v.  We and you acknowledge that in the event of any third-party claim that
      the App or your possession and use of the App infringes that third party’s
      intellectual property rights, as between Sorenson and the Third Party App
      Store, Sorenson, not the Third Party App Store, will be solely responsible
      for the investigation, defense, settlement and discharge of any such
      intellectual property infringement claim to the extent required by this
      Agreement.
    </p>
    <p>
      vi.  We and you acknowledge and agree that the Third Party App Store, and
      their subsidiaries, are third-party beneficiaries of this Agreement as
      related to your use of the Services, and that, upon your acceptance of the
      terms and conditions of this Agreement, the Third Party App Store will have
      the right (and will be deemed to have accepted the right) to enforce this
      Agreement as related to your use of the Services against you as a
      third-party beneficiary thereof.
    </p>
    <p>
      vii.  Without limiting any other terms of this Agreement, you must comply
      with all applicable third-party terms of agreement when using the App.
    </p>
    <p>
      <strong>6. Fees and Payments</strong>
    </p>
    <p>
      <strong>a.  Fees.</strong> You may purchase access to the Service on a
      fixed-term, fixed number of hours or a recurring subscription basis.  Fees
      for the Services (“<strong>Fees</strong>”) are payable by credit card or
      other payment method permitted by us from time to time, in U.S. dollars. You
      authorize us to charge your credit card or bank account for all Fees at the
      start of the applicable Plan Period specified in the Plan you selected. You
      agree to provide and maintain accurate and lawful billing information on
      your Account.  You represent and warrant to Sorenson that all payment
      information you provide is accurate and that you are authorized to use the
      applicable payment instrument. You further authorize us to use a third party
      to process your payments, and consent to the disclosure of your payment
      information to such third party. You agree that we may charge, and you will
      pay to us, any fee or penalty that is assessed or charged to us for a
      “Chargeback” resulting from a failure or refusal of your selected method of
      payment to make a required payment. By choosing a subscription Plan with a
      recurring payment feature, you accept responsibility for all recurring
      charges owed for the Services under the subscription Plan, which may
      increase per renewal to the then-current fee for such Plan as set out on the
      Plan description located on our Website. All Fees are exclusive of any
      country, province, federal, state or local taxes (“<strong>Taxes</strong>”).
      You will be solely liable for and will pay upon demand all Taxes associated
      with your access to and use of the Services (but will not be responsible for
      taxes based on Sorenson’s net income).
    </p>
    <p>
      <strong>b.  Payments Through Third Party App Store.</strong> For payments
      processed by the Third Party App Store and not directly by Sorenson, the
      Fees will be charged to your payment method on file with the Third Party App
      Store upon confirmation of your subscription purchase. Thereafter, the Fees
      and any applicable tax and service fees will be charged to your payment
      method on file with the Third Party App Store on a recurring basis in
      accordance with your selected subscription Plan. You are responsible for all
      charges incurred in connection with your Account. Any inquiries related to
      such payments should be directed to the Third Party App Store.  You may
      update your payment method with the Third Party App Store directly. If at
      any time the Third Party App Store is unable to charge your chosen payment
      method for the Fees due to insufficient funds, expired or invalid account
      details, or otherwise, you remain responsible for payment of such Fees
      directly to Sorenson.
    </p>
    <p>
      <strong>c.  Refunds.</strong> Refunds will not be provided for any
      subscription upon your cancellation. You are responsible for the full
      payment of Fees for the subscription period, even if you cancel your
      subscription before the end of the subscription period. We will not provide
      credit, refunds or prorated refunds for subscriptions that are cancelled or
      downgraded by you mid-subscription period. In such a circumstance, you will
      continue to have access to your prior subscription until the end of the
      then-current subscription period. If we terminate your subscription for a
      breach of the terms of this Agreement, you will NOT be eligible for any
      refund of any Fees.
    </p>
    <p>
      <strong>7. Subscription Plan Period and Termination/Suspension</strong>
    </p>
    <p>
      <strong>
        a.  Subscription Plan Period and Renewal; Upgrades and Downgrades.
      </strong>
      For Participants, access to the Services will last only for the duration of
      the applicable session(s) to which the Participant has been invited.  For
      Registered Users, access to the Services will begin on the date that the
      Registered User signs up to the applicable Plan for the Services and lasts
      for the duration specified in the applicable Plan.  Free Trial Plans
      continue for 30 days, Professional Plans are subscription Plans that renew
      annually, and Pay-as-you-go Plans continue until the hours are used (each a
      “<strong>Plan Period</strong>”); provided that unused hours purchased under
      Pay-as-you-go Plans expire 12 months after purchase. The Professional Plan
      will automatically renew for successive periods equal to the initial paid
      subscription Plan Period unless terminated by either party prior to the next
      renewal date as permitted below, or unless you change to an alternative
      Plan.  To cancel your subscription: (a) if purchased through the Apple App
      Store, follow the
      <a
          href="https://support.apple.com/en-asia/guide/app-store/fire5f3a0745/mac#:~:text=Click%20Account%20Settings%20at%20the,the%20cancellation%2C%20then%20click%20Done."
      >
        standard subscription cancellation process determined by Apple
      </a>
      , (b) if purchased through the Google Play Store, follow the
      <a
          href="https://support.google.com/googleplay/answer/7018481?hl=en&amp;co=GENIE.Platform%3DAndroid"
      >
        standard subscription cancellation process determined by Google
      </a>
      , or (c) for subscriptions through a browser go to
      <a :href="forumUrl">
        {{ forumUrl }}
      </a>
      , log in to your Account, and use the “Cancel my subscription” or “Delete my
      account” options in your Profile.  If you cancel your subscription, your
      Account will remain active and you may continue to use your purchased
      allotment until the end of the current Plan Period, If you delete your
      Account, your will no longer have access to your Account or Plan.  If you
      upgrade your Pay-as-you-go Plan to a Professional Plan, your Professional
      Plan Period will begin immediately upon payment of Fees.    If you wish to
      downgrade a Professional Plan to a Pay-as-you-go Plan, then you must cancel
      your current Professional Plan subscription as described above, and purchase
      new Pay-as-you-go Plan hours.  Your cancelled Professional Plan allotment
      will continue to remain available until expiration of your Professional Plan
      Period.  You understand that we will not refund any Fees paid for your
      previous Plan upon cancellation, Account deletion, Plan upgrade or Plan
      downgrade.
    </p>
    <p>
      <strong></strong>
    </p>
    <p>
      <strong>b.  Suspension of Services; Termination.</strong> We may suspend
      your right to use any Services or terminate this Agreement in its entirety
      for cause: (i) if you are in breach of your payment obligations or any other
      provision (excluding Sections 4.b, 4.c or 8.f) of this Agreement and  fail
      to cure such breach within thirty (30) days after written notice thereof, or
      (ii) immediately if you have violated, or if we have reason to believe you
      have violated, or have encouraged others to violate Sections 4.b, 4.c, or
      8.f.  You may terminate this Agreement at any time, provided that your
      termination of Services mid-Subscription Period will become effective upon
      the expiration of the then-current Subscription Period.
    </p>
    <p>
      <strong>c.  Effect of Suspension.</strong> Upon our suspension of your use
      of or access to the Services: (i) Fees will continue to accrue for the
      Services during the Plan Period, notwithstanding the suspension, (ii) you
      remain liable for all Fees incurred through the then-current Plan Period,
      and (iii) all of your rights with respect to the Services will be suspended
      during the period of the suspension.
    </p>
    <p>
      <strong>d.  Effect of Termination; Survival.</strong> Upon the expiration or
      termination of the Agreement: (a) we will terminate your and/or applicable
      Participants’ access to your User Content through the Services; (b) your
      rights to access and use the Services hereunder will terminate, and you will
      immediately cease all use of the Services. Any section of the Agreement
      that, by its terms or its nature, should survive the termination or
      expiration of the Agreement will so survive, including Sections 6, 7.c, 7.d,
      and 8 through 24, and (c) we will delete any content uploaded to the storage
      provided as part of your Plan.
    </p>
    <p>
      <strong>8. Intellectual Property Rights</strong>
    </p>
    <p>
      <strong>a.  Service.</strong> As between us and you, we and/or our
      licensors, own and reserve all rights, title and interest in and to the
      Service, App, Website, and Documentation, including all improvements,
      modifications and derivative works thereof, and all hardware, software and
      other items used to provide the Service, App and Website other than the
      rights explicitly granted to you to use the Service during the Plan Period
      in accordance with these TOS. No title to, or ownership of, any proprietary
      rights related to the Service, App, Website or Documentation is transferred
      to you pursuant to these TOS. All rights not explicitly granted to you are
      reserved by Sorenson.
    </p>
    <p>
      <strong>b. Suggestions and Feedback.</strong> If you provide or submit to us
      any ideas, suggestions, proposals, feedback or improvements (“<strong>Feedback</strong>”)
      relating to our Services or other products or services by any means, such as
      through the methods indicated in the “Contact Us” Section below, by email or
      other communication channels, or to our customer support or other personnel,
      we are under no obligation of confidentiality, express or implied with
      respect to the Feedback.  We (and our affiliates, subcontractors and
      assigns) are entitled to use or disclose (or choose not to use or disclose)
      such Feedback in any way and for any purpose.  Such Feedback will
      automatically become our property without any obligations, and you are not
      entitled to any accounting, compensation or reimbursement of any kind from
      us under any circumstances.
    </p>
    <p>
      <strong>c.  User Content.</strong> "<strong>User Content</strong>" means,
      without limitation, any spoken words, phases, audio, messages, text,
      reviews, comments, feedback, suggestions, music, video, digital files,
      images, photos, artwork, hyperlinks and documents, or any other content you
      upload, transmit or otherwise make available to us and/or the Users via the
      Services. You represent and warrant that you own or otherwise control the
      rights to your User Content and that each and every part thereof is an
      original work by you, or you have obtained all rights, licenses, consents
      and permissions necessary in order to use those parts at any and all times.
      You further agree to indemnify the Sorenson Parties (as defined below) for
      all claims arising from or in connection with any claims to any rights in
      your User Content, or any damages arising from our use of your User Content
      as permitted herein.  By transmitting or submitting User Content on or
      through the Service, you grant to Sorenson and its affiliates a perpetual,
      worldwide, non-exclusive, royalty-free license (with the right to
      sublicense) to translate, interpret, use, re-use, broadcast, stream,
      download, reproduce, transmit, print, publish, display, exhibit, distribute,
      re-distribute, copy, host, store, cache, archive, index, categorize, edit,
      alter, modify, adapt, create derivative works based upon and to publicly
      perform such User Content (a) as necessary to provide the Services to you in
      accordance with these TOS; (b) to perform necessary maintenance,
      calibration, diagnostic and troubleshooting of the Services, and to monitor
      the performance of the Services; (c) to improve our (and our affiliates’)
      products and services, including to train and maintain our (and our
      affiliates’) translation and transcription and other artificial intelligence
      models; (d) to perform such other actions as authorized or instructed by you
      in writing (email to suffice); and (e) as required by law, without
      attribution, and without the requirement of any permission from, or payment
      to, you or to any other person or entity. You understand that the Service is
      intended for live transcription and translation to a group audience and
      agree that any User Content you submit to our Service may be heard or viewed
      by your Participants, other Users, or by any member the public in general
      who obtains the applicable session code. As such, User Content is not
      considered to be confidential. You agree not to transmit or submit User
      Content in which you have any expectation of privacy or confidentiality.
    </p>
    <p>
      <strong>d.  Responsibility for User Content.</strong> We do not own the User
      Content, and you, not us, will have sole responsibility for the accuracy,
      quality, integrity, legality, reliability, appropriateness, and intellectual
      property ownership or right to use of the User Content. In connection with
      the User Content, you represent and warrant that: (a) you have the written
      consent of each and every natural person identifiable in the User Content to
      use such person’s name or likeness in the manner contemplated by the
      Services and the Agreement; (b) you have obtained (and are solely
      responsible for obtaining) all consents that may be required by applicable
      provisions of foreign, federal, state or local laws, rules, regulations or
      orders of all governmental bodies, courts, tribunals and arbitrators
      (“<strong>Applicable Law</strong>”) to submit the User Content; (c) the User
      Content and the provision and use of the User Content does not otherwise
      violate these TOS; and (d) the User Content and our use thereof as
      contemplated by these TOS and the Services will not violate any law or
      infringe any rights of any third party, including but not limited to any
      intellectual property rights and/or privacy rights. We expressly disclaim
      any liability for the data and content transmitted through or
      intermediately, temporarily or permanently stored on Sorenson networks or
      any server and for your (or your invited Participants’) actions or
      omissions. You acknowledge and agree that (i) Sorenson need not pre-screen
      the User Content, although Sorenson reserves the right in our reasonable
      discretion to pre-screen, refuse or remove any of the User Content; and (ii)
      you are responsible for ensuring that you back up the User Content.  THE APP
      AND SERVICES MAY PROVIDE YOU WITH THE ABILITY TO SAVE CERTAIN TRANSLATIONS,
      INCLUDING WORDS AND PHRASES, ("<strong>SAVED CONTENT</strong>"), HOWEVER
      SORENSON EXPRESSLY DISCLAIMS ANY LIABILITY FOR THE LOSS OR DAMAGE TO ANY
      SAVED CONTENT OR ANY LOSSES OR DAMAGES YOU INCUR AS A RESULT OF THE LOSS OR
      DAMAGE OF ANY SAVED CONTENT.
    </p>
    <p>
      <strong>e.  Our Right to Remove User Content.</strong> We have the absolute
      right to remove or disable access to any content on the Services, including
      without limitation, the User Content as needed (a) to operate, secure and
      improve the Services (including without limitation for fraud prevention,
      risk assessment, investigation and customer support purposes); (b) to ensure
      compliance with the TOS (or any Sorenson policy), Applicable Law, or an
      order or requirement of a court, law enforcement or other administrative
      agency or governmental body; or (c) as otherwise set forth in this TOS. If
      we become aware of any content posted through the Service that allegedly
      violates these TOS, we may investigate the allegation and determine in our
      sole discretion whether to act but have no liability or responsibility to
      you to do so. You agree to cooperate with us in good faith, as we may
      reasonably request, in any investigation we choose to undertake with respect
      to the User Content.
    </p>
    <p>
      <strong>f.  Prohibited Content.</strong> You will not nor permit others to
      submit, upload, email, transmit or otherwise make available through the
      Service any User Content which: (a) is not owned by you or for which you do
      not have all necessary authorization to make available through the Service,
      including in accordance with all applicable intellectual property laws and
      data laws, regulations and privacy standards; (b) constitutes sensitive
      financial data (e.g., bank account numbers, credit card or debit card
      numbers, passwords, and other access codes for financial accounts; (c)
      constitutes personal health information subject to protection under the
      Health Insurance Portability and Accountability Act of 1996 (“<strong>HIPAA</strong>”);
      (d) violates Applicable Law or creates a security risk to any person (e.g.,
      by exposing sensitive personal information); (e) is illegal (including but
      not limited to obscenity, sexual content involving minors); or (f) a
      reasonable person would find abusive, harassing, threatening, defamatory,
      libelous, obscene, or otherwise objectionable.
    </p>
    <p>
      <strong></strong>
    </p>
    <p>
      <strong>9.</strong> <strong>Confidentiality.</strong>
    </p>
    <p>
      You may not disclose our Confidential Information to any other person, and
      you may not use any Confidential Information except for the purposes of
      using the Services. Except as otherwise provided in this Agreement, you may
      not, without our prior written consent, at any time, during or after the
      Term of this Agreement, directly or indirectly, divulge or disclose
      Confidential Information for any purpose.  You will hold all Confidential
      Information in strict confidence and take all measures necessary to prevent
      unauthorized copying, use, or disclosure of Confidential Information, and to
      keep the Confidential Information from falling into the public domain or
      into the possession of persons not bound to maintain its confidentiality.
      You will promptly advise us in writing of any improper disclosure,
      misappropriation, or misuse of the Confidential Information by any person,
      which may come to your attention. You agree that we will suffer irreparable
      harm if you fail to comply with your obligations set forth in this Section
      9, and you further agree that monetary damages will be inadequate to
      compensate us for any such breach. Accordingly, you agree that we will, in
      addition to any other remedies available to us at law or in equity, be
      entitled to the issuance of injunctive relief to enforce the provisions
      hereof, immediately and without the necessity of posting a bond. “<strong>Confidential
      Information</strong>” means any information relating to our business,
      financial affairs, current or future products or technology, trade secrets,
      workforce, customers, or any other information that is treated or designated
      by us as confidential or proprietary or would reasonably be viewed as
      confidential or as having value to our competitors. Confidential Information
      does not include information that we make publicly available or that becomes
      known to the general public other than as a result of any inaction or action
      by you. Confidential Information does not include individuals' health
      information.
    </p>
    <p>
      <strong>10. Electronic Communications</strong>
    </p>
    <p>
      We may choose to communicate with you by electronic means including, without
      limitation, email or by posting notices on our Services.  When you use our
      Services, you consent to communicating with us electronically. You agree
      that all agreements, notices, disclosures and other communications that we
      provide to you electronically satisfy any legal requirement that such
      communications be in writing.
    </p>
    <p>
      <strong>11.  Copyright and Intellectual Property Policy</strong>
    </p>
    <p>
      We respect the intellectual property rights of others. We reserve the right
      to (a) remove any User Content on the Services which allegedly infringe upon
      another person's copyright, trademark or other intellectual property right,
      (b) terminate, discontinue, suspend and/or restrict your Account or ability
      to access and/or use the Services; and/or remove, edit, or disable any User
      Content on the Services which allegedly infringe upon another person's
      intellectual property rights. We provide this policy pursuant to Section 512
      of the Copyright Revision Act, as enacted through the Digital Millennium
      Copyright Act ("<strong>DMCA</strong>").
    </p>
    <p>
      If you believe that your work has been copied in a way that constitutes
      copyright infringement, please provide us with the following information:
    </p>
    <ul>
      <li>
        A physical or electronic signature of the copyright owner or a person
        authorized to act on behalf of the owner, if someone other than you, of
        an exclusive right that is allegedly infringed;
      </li>
      <li>
        Identification of the copyrighted work claimed to have been infringed;
      </li>
      <li>
        Identification of the material that is claimed to be infringing or to be
        the subject of infringing activity and that is to be removed or access
        to which is to be disabled, and information reasonably sufficient to
        permit us to locate the material;
      </li>
      <li>
        A statement by you that you have a good faith belief that use of the
        material in the manner complained of is not authorized by the copyright
        owner, its agent, or the law;
      </li>
      <li>
        A statement that the information in the notification is accurate, and,
        under penalty of perjury, that you are authorized to act on behalf of
        the copyright owner; and
      </li>
      <li>
        Your contact information, including your address, telephone number, and
        email address.
      </li>
    </ul>
    <p>
      We reserve the right to remove User Content alleged to be infringing without
      prior notice, at our sole discretion, and without liability to you. In
      appropriate circumstances, we will also terminate your Account if you are
      determined to be a repeat infringer.
    </p>
    <p>
      If you wish to reach our designated agent regarding copyright issues, please
      contact:
    </p>
    <p>
      Sorenson Communications, LLC
    </p>
    <p>
      Attn: Designated DMCA Agent
    </p>
    <p>
      4192 S. Riverboat Road,
    </p>
    <p>
      Salt Lake City, UT 84123
    </p>
    <p>
      legalnotices@sorenson.com
    </p>
    <p>
      <strong>12. Disclaimers; No Warranties</strong>
    </p>
    <p>
      <strong>a.</strong> <strong>Disclaimers.</strong> ALL SOFTWARE AND SERVICES
      PROVIDED BY US ARE PROVIDED “AS IS” AND “AS AVAILABLE” WITH ALL FAULTS AND
      WITHOUT WARRANTY OF ANY KIND. TO THE FULLEST EXTENT PERMISSIBLE BY
      APPLICABLE LAW, WE AND OUR AFFILIATES AND OUR AND THEIR RESPECTIVE OFFICERS,
      DIRECTORS, EMPLOYEES AND AGENTS, (COLLECTIVELY, THE “<strong>SORENSON
      PARTIES</strong>”) DISCLAIM ALL WARRANTIES OF ANY KIND, WHETHER EXPRESS,
      IMPLIED, STATUTORY OR OTHERWISE, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
      WARRANTIES OF MERCHANTABILITY, SATISFACTORY QUALITY, FITNESS FOR A
      PARTICULAR PURPOSE, TITLE, ACCURACY, AND NON-INFRINGEMENT.
    </p>
    <p>
      <strong>b.</strong> <strong>Additional Disclaimers.</strong> WITHOUT
      LIMITING THE FOREGOING, WE MAKE NO WARRANTY THAT THE SERVICES WILL MEET YOUR
      REQUIREMENTS, OR THAT THE SERVICES WILL BE UNINTERRUPTED, ERROR-FREE,
      SECURE, RELIABLE, WITHOUT DELAY, CURRENT, COMPLETE OR THAT DEFECTS IN THE
      SOFTWARE, SERVICES AND PRODUCTS WILL BE CORRECTED. NO ORAL OR WRITTEN
      INFORMATION OR ADVICE GIVEN BY SORENSON OR ITS AUTHORIZED REPRESENTATIVE
      SHALL CREATE A WARRANTY.   YOU EXPRESSLY AGREE THAT YOUR USE OF THE SERVICES
      IS AT YOUR SOLE RISK. YOU ARE SOLELY RESPONSIBLE FOR ANY AND ALL ACTS OR
      OMISSIONS TAKEN OR MADE IN RELIANCE ON THE SERVICES OR THE INFORMATION IN
      THE SERVICES, INCLUDING INACCURATE OR INCOMPLETE INFORMATION. SPECIFICALLY,
      THE SERVICES WILL NOT WORK DURING A POWER OUTAGE BECAUSE THEY ARE DEPENDENT
      UPON THE INTERNET. SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OF IMPLIED
      WARRANTIES OR LIMITATIONS ON APPLICABLE STATUTORY RIGHTS OF A CONSUMER, SO
      IN THESE JURISDICTIONS THE ABOVE EXCLUSIONS AND LIMITATIONS MAY NOT APPLY TO
      YOU.
    </p>
    <p>
      <strong></strong>
    </p>
    <p>
      <strong>c.</strong> <strong>Carrier Lines and Lost Data.</strong> YOU
      ACKNOWLEDGE THAT ACCESS TO THE SERVICES WILL BE PROVIDED OVER VARIOUS
      FACILITIES AND COMMUNICATIONS LINES, AND INFORMATION WILL BE TRANSMITTED
      OVER LOCAL EXCHANGE AND INTERNET BACKBONE CARRIER LINES AND THROUGH ROUTERS,
      SWITCHES, AND OTHER DEVICES (COLLECTIVELY, “<strong>CARRIER LINES</strong>”)
      OWNED, MAINTAINED, AND SERVICED BY THIRD-PARTY CARRIERS, UTILITIES, AND
      INTERNET SERVICE PROVIDERS, ALL OF WHICH ARE BEYOND OUR CONTROL. WE ASSUME
      NO LIABILITY FOR, OR RELATING TO, THE INTEGRITY, PRIVACY, SECURITY,
      CONFIDENTIALITY, OR USE OF ANY INFORMATION WHILE IT IS TRANSMITTED ON THE
      CARRIER LINES, OR ANY DELAY, FAILURE, INTERRUPTION, INTERCEPTION, LOSS,
      TRANSMISSION, OR CORRUPTION OF ANY DATA OR OTHER INFORMATION ATTRIBUTABLE TO
      TRANSMISSION ON THE CARRIER LINES. USE OF THE CARRIER LINES IS SOLELY AT
      YOUR RISK AND IS SUBJECT TO ALL APPLICABLE LOCAL, STATE, NATIONAL, AND
      INTERNATIONAL LAWS.  YOU HEREBY WAIVE ANY DAMAGES OCCASIONED BY LOST OR
      CORRUPT DATA, INCORRECT TRANSLATIONS OR REPORT, OR INCORRECT DATA FILES
      RESULTING FROM PROGRAMMING ERROR, OPERATOR ERROR, EQUIPMENT OR SOFTWARE
      MALFUNCTION, SECURITY VIOLATIONS, OR THE USE OF THIRD-PARTY SOFTWARE. WE ARE
      NOT RESPONSIBLE FOR THE CONTENT OF ANY INFORMATION TRANSMITTED OR RECEIVED
      THROUGH THE SERVICES.
    </p>
    <p>
      <strong>13. Limitation of Liability</strong>
    </p>
    <p>
      <strong>a.</strong> <strong>Type of Damages.</strong> TO THE EXTENT NOT
      PROHIBITED BY LAW, IN NO EVENT SHALL THE SORENSON PARTIES BE RESPONSIBLE TO,
      OR LIABLE TO YOU, OR ANY THIRD PARTY,  FOR ANY SPECIAL, INCIDENTAL,
      INDIRECT, EXEMPLARY OR CONSEQUENTIAL DAMAGES INCLUDING, BUT NOT LIMITED TO,
      LOSS OF USE, LOSS OF PROFIT, REVENUE OR BUSINESS, LOSS OF GOODWILL, OR LOSS
      OF INFORMATION OR DATA, WHETHER A CLAIM FOR ANY SUCH LIABILITY OR DAMAGES IS
      PREMISED UPON BREACH OF CONTRACT, BREACH OF WARRANTY, NEGLIGENCE, STRICT
      LIABILITY, OR ANY OTHER THEORY OF LIABILITY, EVEN IF WE HAVE BEEN APPRISED
      OF THE POSSIBILITY OR LIKELIHOOD OF SUCH DAMAGES.
    </p>
    <p>
      <strong>b.  Direct Damages Limitation.</strong> OUR LIABILITY AND THE
      LIABILITY OF THE SORENSON PARTIES ARISING OUT OF THIS AGREEMENT SHALL NOT
      EXCEED THE LESSER OF: (1) THE SUM OF $100, AND (2) THE AMOUNT OF FEES
      ACTUALLY PAID BY YOU IN THE ONE MONTH IMMEDIATELY PRECEDING THE CLAIM.
    </p>
    <p>
      <strong></strong>
    </p>
    <p>
      <strong>c.  Limitation on Actions.</strong> YOU AGREE THAT ANY CAUSE OF
      ACTION ARISING OUT OF OR RELATED TO OUR SERVICES MUST COMMENCE WITHIN ONE
      (1) YEAR AFTER THE CAUSE OF ACTION ACCRUES. OTHERWISE, SUCH CAUSE OF ACTION
      IS PERMANENTLY BARRED.
    </p>
    <p>
      <strong></strong>
    </p>
    <p>
      <strong>d.  Enforceability.</strong> CERTAIN STATE OR JURISDICTIONAL LAWS DO
      NOT ALLOW LIMITATIONS ON IMPLIED WARRANTIES OR THE EXCLUSION OR LIMITATION
      OF CERTAIN DAMAGES. IF THESE LAWS APPLY TO YOU, SOME OR ALL OF THE ABOVE
      DISCLAIMERS, EXCLUSIONS OR LIMITATIONS MAY NOT APPLY TO YOU.
    </p>
    <p>
      <strong>14. Indemnity</strong>
    </p>
    <p>
      You will indemnify, defend and hold harmless the Sorenson Parties from and
      against any and all actions, proceedings, judgments, claims, liabilities,
      damages, penalties, losses and expenses, including reasonable attorneys’ and
      accounting fees and costs, arising out of or in any way connected with (i)
      your access to, use of or alleged use of the Services,  Plan or products you
      purchase through the Services; (ii) your violation of these TOS or any
      representation, warranty, or covenants contained herein, or any applicable
      law or regulation; (iii) the User Content or Sorenson’s use thereof as
      permitted herein; (iv) your violation of any rights of a third party; or (v)
      any emotional or physical harm, including death, to you or any third party
      resulting from your access to or use of the Services, products or Plans
      purchased though the Service.
    </p>
    <p>
      <strong>15. Our Remedies</strong>
    </p>
    <p>
      You acknowledge that we may be irreparably damaged if this Agreement is not
      specifically enforced, and damages at law would be an inadequate remedy.
      Therefore, in the event of a breach or threatened breach of any provision of
      this Agreement by you, we shall be entitled, in addition to all rights and
      remedies, to an injunction restraining such breach or threatened breach,
      without being required to show any actual damage or to post an injunction
      bond, and/or to a decree for specific performance of the provisions of this
      Agreement.
    </p>
    <p>
      <strong>
        16.  Dispute Resolution; Waiver of Jury Trial; Governing Law;
        Jurisdiction
      </strong>
    </p>
    <p>
      <strong>
        PLEASE READ THIS SECTION CAREFULLY. THIS SECTION CONTAINS AN AGREEMENT
        TO ARBITRATE, WHICH WILL REQUIRE YOU TO SUBMIT CLAIMS YOU HAVE AGAINST
        US TO BINDING AND FINAL ARBITRATION AND AN AGREEMENT THAT YOU WILL ONLY
        BE PERMITTED TO PURSUE CLAIMS AGAINST US ON AN INDIVIDUAL BASIS, NOT AS
        A PLAINTIFF OR CLASS MEMBER IN ANY CLASS OR REPRESENTATIVE ACTION OR
        PROCEEDING.
      </strong>
    </p>
    <p>
      Unless otherwise prohibited by applicable law, you and we agree that any
      claim or dispute at law or equity that has arisen, or may arise, between you
      and us (including any claim or dispute between you and a third-party agent
      of ours) that relates in any way to or arises out of this or previous
      versions of this Agreement, your use of or access to the Services, the
      actions of us or our agents, or any products or services sold or purchased
      through the Services, will be resolved in accordance with the provisions set
      forth in this Dispute Resolution Section.
    </p>
    <p>
      <strong>a.</strong> <strong>Pre-Arbitration Dispute Resolution.</strong> We
      at Sorenson believe that most customer concerns can be resolved quickly and
      to the customer's satisfaction by emailing customer support at
      <a :href="`mailto:${supportEmail}`">
        {{ supportEmail }}
      </a>
      . If such efforts prove unsuccessful, a party who intends to seek
      arbitration must first send to the other, by certified mail, a written
      Notice of Dispute ("<strong>Notice</strong>"). The Notice to us should be
      sent to the address provided in the Contact Us section at the end of these
      TOS ("<strong>Notice Address</strong>"). The Notice must (i) describe the
      nature and basis of the claim or dispute and (ii) set forth the specific
      relief sought. If we and you do not resolve the claim within sixty calendar
      days after the Notice is received, you or we may commence an arbitration
      proceeding. During the arbitration, the amount of any settlement offer made
      by us or you shall not be disclosed to the arbitrator during the arbitration
      proceeding.
    </p>
    <p>
      <strong></strong>
    </p>
    <p>
      <strong>b.  Agreement to Arbitrate.</strong> EXCEPT FOR REMEDIES SOUGHT BY
      SORENSON UNDER SECTION 15 OF THESE TOS, ANY DISPUTE, CLAIM OR CONTROVERSY
      ARISING OUT OF OR RELATING TO THESE TOS OR THE BREACH, TERMINATION,
      ENFORCEMENT, INTERPRETATION OR VALIDITY THEREOF, INCLUDING THE DETERMINATION
      OF THE SCOPE OR APPLICABILITY OF THIS AGREEMENT TO ARBITRATE, SHALL BE
      SUBJECT TO FINAL AND BINDING ARBITRATION GOVERNED BY THE FEDERAL ARBITRATION
      ACT (9 U.S.C. §§ 1 ET SEQ.).  THE ARBITRATION SHALL BE CONDUCTED BEFORE A
      SINGLE ARBITRATOR IN ACCORDANCE WITH THE COMMERCIAL DISPUTE RESOLUTION
      PROCEDURES AND THE SUPPLEMENTARY PROCEDURES FOR CONSUMER RELATED DISPUTES OF
      THE AMERICAN ARBITRATION ASSOCIATION (THE “<strong>AAA</strong>”) THEN IN
      EFFECT, AS MODIFIED BY THESE TOS, AND WILL BE ADMINISTERED BY THE AAA.
      JUDGMENT ON THE AWARD MAY BE ENTERED IN ANY COURT HAVING JURISDICTION.  THIS
      SECTION SHALL NOT PRECLUDE EITHER PARTY FROM SEEKING TEMPORARY OR
      PRELIMINARY INJUNCTIVE RELIEF IN CONNECTION WITH AN ARBITRABLE CONTROVERSY,
      BUT ONLY UPON THE GROUND THAT THE AWARD TO WHICH THAT PARTY MAY BE ENTITLED
      MAY BE RENDERED INEFFECTUAL WITHOUT SUCH PROVISIONAL RELIEF.
    </p>
    <p>
      <strong>
        c.  Prohibition of Class and Representative Actions and
        Non-Individualized Relief.
      </strong>
      UNLESS OTHERWISE PROHIBITED BY APPLICABLE LAW, THE PARTIES AGREE THAT EACH
      PARTY MAY BRING CLAIMS AGAINST THE OTHER ONLY IN AN INDIVIDUAL CAPACITY, AND
      NOT AS A PLAINTIFF, CLASS MEMBER, OR PRIVATE ATTORNEY GENERAL IN ANY
      PURPORTED CLASS, REPRESENTATIVE, OR PRIVATE-ATTORNEY-GENERAL PROCEEDING.  BY
      ENTERING INTO THIS AGREEMENT, YOU AND WE ARE EACH WAIVING THE RIGHT TO A
      JURY TRIAL OR TO PARTICIPATE IN A CLASS ACTION.  THE ARBITRATOR MAY NOT
      CONSOLIDATE MORE THAN ONE PERSON’S CLAIM WITH YOUR CLAIMS OR OUR CLAIMS AND
      MAY NOT OTHERWISE PRESIDE OVER ANY REPRESENTATIVE, CLASS, OR PRIVATE
      ATTORNEY GENERAL PROCEEDING.  THE ARBITRATOR MAY AWARD DECLARATORY OR
      INJUNCTIVE RELIEF ONLY IN FAVOR OF THE INDIVIDUAL PARTY SEEKING RELIEF AND
      ONLY TO THE EXTENT NECESSARY TO PROVIDE RELIEF WARRANTED BY THAT PARTY’S
      INDIVIDUAL CLAIM).
    </p>
    <p>
      <strong>d.  Confidentiality.</strong> All aspects of the arbitration
      proceeding, and any ruling, decision, or award by the arbitrator, will be
      strictly confidential for the benefit of all parties.
    </p>
    <p>
      <strong>e.  Governing Law.</strong> You agree that, except to the extent
      inconsistent with or preempted by federal law, the laws of the State of
      Delaware, without regard to principles of conflict of laws, will govern this
      Agreement and any claim or dispute that has arisen or may arise between you
      and us, except as otherwise stated in this Agreement.
    </p>
    <p>
      <strong></strong>
    </p>
    <p>
      <strong>f.  Jurisdiction for Legal Disputes.</strong> Unless you and we
      agree otherwise, in the event that the agreement to arbitrate above is found
      not to apply to you or to a particular claim or dispute as a result of a
      decision by the arbitrator or a court order, you agree that any claim or
      dispute that has arisen or may arise between you and us must be resolved
      exclusively by a state or federal court located in the State of Utah. You
      and we agree to submit to the personal jurisdiction and venue of the courts
      located within the State of Utah for the purpose of litigating all such
      claims or disputes.
    </p>
    <p>
      <strong>17.  Law Enforcement</strong>
    </p>
    <p>
      We are committed to cooperating with law enforcement while respecting each
      individual’s right to privacy. If Sorenson receives a request for user
      account information from a government agency investigating criminal
      activity, we will review the request to be certain that it satisfies all
      legal requirements before releasing information to the requesting agency.
      Furthermore, under 18 U.S.C. §§ 2702(b)(8) and 2702(c)(4) (Voluntary
      Disclosure of Customer Communications or Records), Sorenson may disclose
      user account information to law enforcement, without a subpoena, court
      order, or search warrant, in response to a valid emergency when we believe
      that doing so is necessary to prevent death or serious physical harm to
      someone. Sorenson will not release more information than it prudently
      believes is necessary to prevent harm or otherwise comply with the law in an
      emergency situation.
    </p>
    <p>
      <strong>18.  Statutory Rights; Notice To California Residents</strong>
    </p>
    <p>
      If you are a California resident, under California Civil Code Section
      1789.3, you may contact the Complaint Assistance Unit of the Division of
      Consumer Services of the California Department of Consumer Affairs in
      writing, currently at: 1625 N. Market Blvd., Suite N-112, Sacramento,
      California 95834, or by telephone, currently at (800) 952-5210 in order to
      resolve a complaint regarding our Service.
    </p>
    <p>
      <strong>19. Severability</strong>
    </p>
    <p>
      If any portion of this Agreement is deemed unlawful, void or unenforceable
      by any arbitrator or court of competent jurisdiction, this Agreement as a
      whole shall not be deemed unlawful, void or unenforceable, but only that
      portion of this Agreement that is unlawful, void or unenforceable shall be
      stricken from this Agreement.
    </p>
    <p>
      <strong>20.  No Waiver</strong>
    </p>
    <p>
      Our failure to enforce any right or provision in these TOS shall not
      constitute a waiver of such right or provision unless acknowledged and
      agreed to by us in writing.
    </p>
    <p>
      <strong>21.  Entire Understanding</strong>
    </p>
    <p>
      These TOS and the Privacy Policy represent the entire understanding and
      agreement between you and us regarding the subject matter of the same, and
      supersede all other previous agreements, understandings and/or
      representations regarding the same.
    </p>
    <p>
      <strong>22.  Assignment</strong>
    </p>
    <p>
      You may not assign or transfer your rights and benefits under these TOS
      without our prior written consent, but we may assign or transfer these TOS
      without restriction.
    </p>
    <p>
      <strong></strong>
    </p>
    <p>
      <strong>23.  Relationship of the Parties</strong>
    </p>
    <p>
      Sorenson is an independent contractor, and this Agreement does not create a
      partnership, joint venture, employment or agency relationship between the us
      and you (or any other Participant).  Except as expressly set forth in this
      Agreement, nothing in this Agreement will be deemed to confer any rights or
      benefits on any third party.
    </p>
    <p>
      <strong>24.  Force Majeure</strong>
    </p>
    <p>
      We will not lose any rights hereunder or be liable to you for damages or
      losses on account of our failure of performance if the failure is due by any
      occurrence or contingency beyond our reasonable control, including, but not
      limited to, war, strike, fire, Act of God, earthquake, flood, lockout,
      embargo, epidemics, pandemics, governmental acts or orders or restrictions,
      failure of suppliers, or any other reason where failure to perform is beyond
      our reasonable control; provided that we will use commercially reasonable
      efforts to promptly mitigate any damages or losses.
    </p>
    <p>
      <strong>25.</strong> <strong>Use of AI</strong>
    </p>
    <p>
      You acknowledge and agree that we may use and develop artificial
      intelligence (“AI”) technologies to provide and support the  Services
      hereunder.  This may include, but is not limited to, content generation,
      automated data analysis, customer support, optimization of Services delivery
      or improving system performance or the Services.  You also acknowledge and
      agree that we may use data collected or derived from the Services
      (including, without limitation, User Content) for the purposes of improving
      or developing AI systems , provided that any such use will be in compliance
      with applicable data protection laws, privacy laws, or laws governing the
      use or development of AI.
    </p>
    <p>
      <strong>CONTACT US:</strong>
    </p>
    <p>
      <strong></strong>
    </p>
    <p>
      If you have to provide us with any Notice, or have any questions, comments,
      concerns or feedback regarding this Agreement or our Services, please
      contact us at
      <a :href="`mailto:${supportEmail}`">
        {{ supportEmail }}
      </a>
      or mail us here:
    </p>
    <p>
      Sorenson Communications, LLC
    </p>
    <p>
      4192 S. Riverboat Road, Salt Lake City, UT 84123
    </p>
    <p>
      Last updated: May 15, 2025
    </p>
  </div>
</template>

<style scoped lang="scss">
    .text-content {
      font-size: 15px;
        
        a {
            color: #6CBFFF;
        }
    }

    p {
      margin: 15px 0;
    }

</style>
<script setup lang="ts">
import { Constants } from '@/cfg/constants';

const termsOfUseUrl = Constants.REGION_CONFIG.termsUrl;
const privacyPolicyUrl = Constants.REGION_CONFIG.privacyUrl;
const websiteUrl = Constants.REGION_CONFIG.websiteUrl;
const forumUrl = Constants.REGION_CONFIG.forumUrl;
const supportEmail = Constants.REGION_CONFIG.supportEmail;
</script>